import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

// Reusable animated photo gallery with a lightbox.
// props: kicker, title, subtitle, images: [{ src, caption }], bg ("light"|"slate")
export const CampusGallery = ({
  kicker = "Gallery",
  title = "Life at Kiki Centre",
  subtitle,
  images = [],
  bg = "light",
  idPrefix = "gallery",
}) => {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(
    () => setActive((i) => (i === null ? i : (i - 1 + images.length) % images.length)),
    [images.length]
  );
  const next = useCallback(
    () => setActive((i) => (i === null ? i : (i + 1) % images.length)),
    [images.length]
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, prev, next]);

  if (!images.length) return null;

  return (
    <section
      className={`py-20 ${bg === "slate" ? "bg-slate-50" : "bg-white"}`}
      data-testid={`${idPrefix}-section`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="kicker mb-3">{kicker}</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0B2A6B] tracking-tight">{title}</h2>
          {subtitle && <p className="mt-4 text-slate-600 leading-relaxed">{subtitle}</p>}
        </div>

        <div className="[column-fill:_balance] gap-4 sm:columns-2 lg:columns-3 xl:columns-4">
          {images.map((img, i) => (
            <motion.button
              key={img.src}
              type="button"
              onClick={() => setActive(i)}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group relative mb-4 block w-full break-inside-avoid overflow-hidden rounded-2xl bg-slate-200 shadow-sm ring-1 ring-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400"
              data-testid={`${idPrefix}-item-${i}`}
            >
              <img
                src={img.src}
                alt={img.caption || "Kiki Centre"}
                loading="lazy"
                className="w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
              />
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0B2A6B]/85 via-[#0B2A6B]/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <span className="pointer-events-none absolute right-3 top-3 flex h-9 w-9 translate-y-2 items-center justify-center rounded-full bg-yellow-400 text-[#0B2A6B] opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                <ZoomIn className="h-4 w-4" />
              </span>
              {img.caption && (
                <span className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 p-4 text-left text-sm font-semibold text-white opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {img.caption}
                </span>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid={`${idPrefix}-lightbox`}
          >
            <button
              onClick={close}
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              data-testid={`${idPrefix}-lightbox-close`}
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 sm:left-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-yellow-400 hover:text-[#0B2A6B]"
              aria-label="Previous"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 sm:right-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-yellow-400 hover:text-[#0B2A6B]"
              aria-label="Next"
            >
              <ChevronRight className="h-7 w-7" />
            </button>

            <motion.figure
              key={active}
              className="max-h-[85vh] max-w-5xl"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[active].src}
                alt={images[active].caption || "Kiki Centre"}
                className="mx-auto max-h-[78vh] w-auto rounded-xl object-contain shadow-2xl"
              />
              {images[active].caption && (
                <figcaption className="mt-4 text-center text-sm font-medium text-slate-200">
                  {images[active].caption}
                  <span className="ml-2 text-slate-500">
                    {active + 1} / {images.length}
                  </span>
                </figcaption>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default CampusGallery;
